import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';

import { useAutoRefresh } from '@/hooks/ui/useAutoRefresh';
import { useFocusWithinPause } from '@/hooks/ui/useFocusWithinPause';
import { formatToDdMmYyyyHHmm } from '@/utils';


export const MONITORING_REFRESH_INTERVAL_MS = 3 * 60 * 1000;

// Bloqueo corto del botón tras cada uso, para evitar el doble clic accidental.
const MANUAL_REFRESH_COOLDOWN_MS = 3 * 1000;

export interface UseMonitoringRefreshConfig {
  /** Habilita el ciclo automático. */
  enabled: boolean;
}

export interface UseMonitoringRefreshResult {
  /** Contenedor cuyo foco interno pausa el ciclo automático. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Cambia en cada refresco; los contadores y las tres tablas reconsultan al verlo cambiar. */
  refreshToken: number;
  /** Última actualización en `dd/mm/yyyy HH:mm`; vacío antes de la primera. */
  lastUpdateLabel: string;
  canManualRefresh: boolean;
  onManualRefresh: () => void;
  /** Sella la hora de la última actualización. */
  onRefreshCompleted: () => void;
}

// Orquesta la actualización del Monitoreo de Asistencia: ciclo automático, refresco bajo demanda y
// sello de la última actualización.
export function useMonitoringRefresh({
  enabled,
}: UseMonitoringRefreshConfig): UseMonitoringRefreshResult {
  const { containerRef, isInteracting } = useFocusWithinPause<HTMLDivElement>();

  const [refreshToken, setRefreshToken] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [canManualRefresh, setCanManualRefresh] = useState(true);
  const cooldownTimerRef = useRef<number | null>(null);

  const triggerRefresh = useCallback((): void => {
    setRefreshToken((previous) => previous + 1);
  }, []);

  useAutoRefresh({
    intervalMs: MONITORING_REFRESH_INTERVAL_MS,
    onRefresh: triggerRefresh,
    paused: isInteracting,
    enabled,
  });

  const onManualRefresh = useCallback((): void => {
    if (!canManualRefresh) return;

    setCanManualRefresh(false);
    if (cooldownTimerRef.current) window.clearTimeout(cooldownTimerRef.current);
    cooldownTimerRef.current = window.setTimeout(() => {
      setCanManualRefresh(true);
    }, MANUAL_REFRESH_COOLDOWN_MS);

    triggerRefresh();
  }, [canManualRefresh, triggerRefresh]);

  useEffect(() => () => {
    if (cooldownTimerRef.current) window.clearTimeout(cooldownTimerRef.current);
  }, []);

  const onRefreshCompleted = useCallback((): void => {
    setLastUpdatedAt(new Date());
  }, []);

  const lastUpdateLabel = useMemo(
    () => (lastUpdatedAt ? formatToDdMmYyyyHHmm(lastUpdatedAt) : ''),
    [lastUpdatedAt],
  );

  return {
    containerRef,
    refreshToken,
    lastUpdateLabel,
    canManualRefresh,
    onManualRefresh,
    onRefreshCompleted,
  };
}

export default useMonitoringRefresh;
